import type { FeedItem } from '~/hooks/queries/use-feed';

/**
 * A served sketch / slide deck / app is a PAGE served by EDDY at
 * `/apps/<slug>/`, not a route inside this SPA, so every doorway to one opens
 * it in a new tab: the Sketches gallery, the chat sketch card, the command
 * palette (FRONT-1974) and the Inbox row for a saved one (FRONT-3339). This is
 * the one place that decides whether a path may be handed to `window.open`.
 *
 * Anything that is not `/apps/…` is refused. A value that reached a
 * `javascript:` or absolute URL would execute in the opener's origin; the wire
 * only ever produces the `/apps/` form today, so this is a whitelist over a
 * value we already trust, the cheap version of the house rule that a security
 * reviewer's flag on caller-supplied input gets dropped or whitelisted, not
 * argued with (Aikido, PR #1490). `//host/…` is protocol-relative and leaves
 * the origin, so it is refused even though a prefix check on `/` would pass it.
 */
export function isServedAppPath(path: string): boolean {
  return path.startsWith('/apps/') && !path.startsWith('//');
}

/**
 * Opens a served page in a new tab and says whether it did: false for a path
 * `isServedAppPath` refuses (nothing is opened).
 *
 * `window.open` runs SYNCHRONOUSLY inside the caller's click handler. A popup
 * opened a frame later (after a `requestAnimationFrame`, a promise or a
 * dialog's close animation) is dropped by the browser's popup blocker and the
 * row would silently do nothing (review panel finding, FRONT-1974). `open` is
 * a parameter only so the plain-node frontend suite can observe the call.
 */
export function openServedApp(
  path: string,
  open: (url: string, target: string, features: string) => unknown = (u, t, f) => window.open(u, t, f),
): boolean {
  if (!isServedAppPath(path)) return false;
  open(path, '_blank', 'noopener,noreferrer');
  return true;
}

/**
 * The path to open for an Inbox `app` item (a sketch, slide deck or app the
 * person asked EDDY to save), or null: not an `app` item, no `appPath`, or a
 * path `isServedAppPath` refuses. The path is the server's `/apps/<slug>/`,
 * used exactly as sent: it is never rebuilt from the slug here, so there is no
 * second spelling of the address to drift from the Sketches gallery's.
 */
export function servedAppHref(item: Pick<FeedItem, 'type' | 'appPath'>): string | null {
  if (item.type !== 'app') return null;
  const path = item.appPath;
  return typeof path === 'string' && isServedAppPath(path) ? path : null;
}

/**
 * What a click on an Inbox `app` row does: open the original in a new tab.
 * Returns false and opens nothing when the item has no safe path, so a broken
 * row can never fall through to the chat window (which would show it as a
 * fake briefing).
 */
export function openServedAppItem(
  item: Pick<FeedItem, 'type' | 'appPath'>,
  open?: Parameters<typeof openServedApp>[1],
): boolean {
  const href = servedAppHref(item);
  return href === null ? false : openServedApp(href, open);
}
